import { useContext, useEffect, useMemo, useState } from 'react';

import { useBooleanState } from '@vyce/core/src/hooks';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { useIsMounted } from '@vyce/core/src/hooks/useIsMounted';
import { TIME_INTERFACE_PERMISSIONS, TopPerformingHours, TopPerformingWorker } from '@vyce/core/src/types';
import {
  getTopPerformingTeamMembersLatWeekRequest,
  getTopPerformingWorkersLastWeekSharedData,
} from '@vyce/core/src/api/time';

import { Props } from '../types';
import { defaultWidgetHours } from '../config';
import { NotificationContext } from '../../../../contexts/notificationContext';
import { useWidgetErrorHandler } from '@vyce/core/src/hooks/useWidgetErrorHandler';
import { generateExcelFileLink } from '../../../../utils';

export const useWidgetData = ({ selectedCompanyId, onForbidden }: Props) => {
  const { canAnywhere } = usePermissions();
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(true);
  const [downloadButtonLoading, setDownloadButtonLoadingTrue, setDownloadButtonLoadingFalse] =
    useBooleanState(false);
  const [topPerformingWorkers, setTopPerformingWorkers] = useState<TopPerformingWorker[]>([]);
  const [topPerformingHours, setTopPerformingHours] = useState<TopPerformingHours>(defaultWidgetHours);
  const { handleServerError } = useContext(NotificationContext);
  // Loading the widget: a 403 hides it instead of raising a toast.
  const handleWidgetError = useWidgetErrorHandler(onForbidden);
  const isMounted = useIsMounted();

  // Held per-location for a scoped manager, so a company-only check would
  // blank the widget for them.
  const cnaViewWorker = useMemo(() => canAnywhere(TIME_INTERFACE_PERMISSIONS.USERS), [canAnywhere]);

  const fetchData = async () => {
    if (!selectedCompanyId) return;
    setLoadingTrue();

    try {
      const { data } = await getTopPerformingTeamMembersLatWeekRequest({
        companyId: selectedCompanyId,
      });

      const { top_performing_workers, ...restData } = data;

      if (!isMounted.current) return;
      setTopPerformingWorkers(top_performing_workers);
      setTopPerformingHours({ ...defaultWidgetHours, ...restData });

      setLoadingFalse();
    } catch (e) {
      handleWidgetError(e);
      if (isMounted.current) setLoadingFalse();
    }
  };

  const getSharedFile = async () => {
    if (!selectedCompanyId) {
      return;
    }
    try {
      setDownloadButtonLoadingTrue();
      const res = await getTopPerformingWorkersLastWeekSharedData({
        companyId: selectedCompanyId,
      });
      generateExcelFileLink(res);
    } catch (e) {
      handleServerError(e);
    } finally {
      if (isMounted.current) setDownloadButtonLoadingFalse();
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedCompanyId]);

  return {
    loading,
    downloadButtonLoading,
    getSharedFile,
    topPerformingWorkers,
    topPerformingHours,
    cnaViewWorker,
  };
};
